import './layout.scss';

import styled from '@emotion/styled';
import React, { useState } from 'react';
import { graphql } from 'gatsby';
import ReactPlayer from 'react-player';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { MarketoForm, useSiteMetadata, SEO } from '@lucid/gatsby-theme-lucid';
import { ThemeProvider } from 'emotion-theming';
import theme from '../theme/theme.ts';
import { getProcessedValue } from '../utils/helpers.ts';

import { languages } from '../../data/languages.ts';
import LayoutContext from '../contexts/layoutContext';

import whiteLogo from '../assets/images/logo.svg';
import xIcon from '../assets/images/x.svg';

import '../stylesheets/_base.scss';

const EmotionLayoutContextProvider = styled(LayoutContext.Provider)({});

const Layout = ({
  langPrefix,
  marketoForms,
  location,
  pageType,
  children,
  metatags,
  otherTranslationData,
  nid
}) => {
  const { cmsUrl } = useSiteMetadata();
  const [modal, setModal] = useState(null);
  const [videoModalUrl, setVideoModalUrl] = useState(null);
  const [videoAutoPlay, setVideoAutoPlay] = useState(false);
  const [videoConfig, setVideoConfig] = useState({});
  const [formData, setFormData] = useState({
    form: null,
    topText: null,
    confirm: null
  });

  const { form, topText, confirm } = formData;

  let showEditButton = false;

  if (typeof window !== 'undefined') {
    showEditButton = window.localStorage.getItem('showEditLink') === '1';
  }

  const openModal = html => {
    setModal(html);
  };

  const closeModal = () => setModal(false);

  const getMarketoModal = (thisFormId, thisTopText = {}, thisConfirm = {}) => {
    const thisForm = marketoForms[thisFormId];

    if (!thisForm) {
      throw Error(
        `Did not find a form with ID ${thisFormId} of type ${typeof thisFormId}`
      );
    }

    setFormData({
      form: thisForm,
      topText: thisTopText,
      confirm: thisConfirm
    });
  };

  const getMarketoFields = (marketoForm, prefix) => {
    let mktoId = null;
    let mktoFields = {};
    if (
      !marketoForm ||
      !('entity' in marketoForm) ||
      !('entityTranslations' in marketoForm.entity)
    ) {
      return { mktoId, mktoFields };
    }
    const match = marketoForm.entity.entityTranslations.find(
      ({ entityLanguage }) => entityLanguage.id === prefix
    );

    if (match) {
      mktoId = match.fieldMarketoId;
      mktoFields = {
        title: match.fieldMarketoTitle,
        body: getProcessedValue(match.fieldMarketoBody),
        note: getProcessedValue(match.fieldMarketoNote)
      };
    }
    return { mktoId, mktoFields };
  };

  const closeMarketoModal = () =>
    setFormData({
      form: null,
      topText: null,
      confirm: null
    });

  const getVideoModal = (
    thisVideoModalUrl,
    autoPlay = false,
    thisVideoConfig = {}
  ) => {
    setVideoModalUrl(thisVideoModalUrl);
    setVideoAutoPlay(autoPlay);
    setVideoConfig(thisVideoConfig);
  };

  const { shortLangPrefix, i18nKey, urlLangPrefix } = languages[langPrefix];

  const layoutContext = {
    language: {
      langPrefix,
      shortLangPrefix,
      urlLangPrefix,
      i18nKey
    },
    location,
    marketo: {
      getMarketoModal,
      getMarketoFields
    },
    video: {
      getVideoModal
    },
    modal: {
      openModal
    }
  };

  const handleCloseModal = e => {
    e.preventDefault();
    setVideoModalUrl('');
  };

  return (
    <EmotionLayoutContextProvider value={layoutContext}>
      <SEO
        entityMetatags={metatags}
        entityTranslations={otherTranslationData}
        pageType={pageType}
        shortLangPrefix={shortLangPrefix}
        location={location}
      />
      <ThemeProvider theme={theme}>
        {children}
        {form !== null && (
          <Modal
            isOpen
            shouldCloseOnOverlayClick
            portalClassName="marketo-modal"
            overlayClassName="marketo-modal__overlay"
            className="marketo-modal__content"
            onRequestClose={closeMarketoModal}
          >
            <div className="marketo-modal__header">
              <img
                className="marketo-modal__logo"
                src={whiteLogo}
                alt="Lucidchart"
              />
              <button
                className="marketo-modal__close"
                type="button"
                onClick={closeMarketoModal}
              >
                ×
              </button>
            </div>
            <div className="marketo-modal__body">
              <MarketoForm
                form={form}
                topText={topText}
                confirm={confirm}
                location={location}
                i18nKey={i18nKey}
                variant="old"
              />
            </div>
          </Modal>
        )}
        {videoModalUrl && (
          <Modal
            isOpen
            ariaHideApp={false}
            onRequestClose={e => handleCloseModal(e)}
            shouldCloseOnOverlayClick
            portalClassName="video-modal"
            overlayClassName="video-modal__overlay"
            className="video-modal__content"
          >
            <>
              <div className="video-modal__container">
                <ReactPlayer
                  url={videoModalUrl}
                  height="100%"
                  width="100%"
                  controls
                  playing={videoAutoPlay}
                  config={videoConfig}
                />
              </div>
              <button
                type="button"
                className="video-modal__button"
                tabIndex={0}
                onClick={e => handleCloseModal(e)}
                onKeyPress={e => handleCloseModal(e)}
              >
                <img
                  src={xIcon}
                  alt="close"
                  className="video-modal__exit"
                  width="20"
                />
              </button>
            </>
          </Modal>
        )}
        {modal && (
          <Modal
            isOpen
            ariaHideApp={false}
            onRequestClose={() => closeModal()}
            shouldCloseOnOverlayClick
            portalClassName="modal"
            overlayClassName="modal__overlay"
            className="modal__content"
          >
            <div className="modal__container">
              {modal}

              <button
                type="button"
                tabIndex={0}
                onClick={() => closeModal()}
                onKeyPress={() => closeModal()}
                className="modal__exit"
              />
            </div>
          </Modal>
        )}

        {showEditButton && (
          <a
            className="cms-edit-link"
            href={`${cmsUrl}/${urlLangPrefix}node/${nid}/edit`}
          >
            Edit Page
          </a>
        )}
      </ThemeProvider>
    </EmotionLayoutContextProvider>
  );
};

Layout.defaultProps = {
  location: {},
  otherTranslationData: [],
  metatags: [],
  marketoForms: [],
  nid: ''
};

Layout.propTypes = {
  langPrefix: PropTypes.string.isRequired,
  otherTranslationData: PropTypes.arrayOf(PropTypes.object),
  metatags: PropTypes.arrayOf(PropTypes.object),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    origin: PropTypes.string
  }),
  pageType: PropTypes.string.isRequired,
  marketoForms: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node.isRequired,
  nid: PropTypes.string
};

export default Layout;

export const query = graphql`
  fragment i18nFile on SrcJson {
    parent {
      ... on File {
        name
      }
    }
  }

  fragment link on Drupal_ParagraphLink {
    fieldOpenNewTab
    fieldCta {
      url {
        path
      }
      title
    }
  }

  fragment marketo on Drupal_ParagraphMarketoForm {
    entityLanguage {
      id
    }
    fieldMarketoId
    fieldMarketoTitle
    fieldMarketoBody {
      processed
    }
    fieldMarketoNote {
      processed
    }
    fieldMarketoCtaText
  }

  fragment menuFragment on Drupal_MenuLinkContent {
    menuName
    parent
    entityUuid
    entityLanguage {
      id
    }
    title
    link {
      url {
        path
      }
    }
    ... on Drupal_MenuLinkContentDefaultFooter {
      fieldKey
      fieldAnalyticsEventName
      fieldMarketo {
        entity {
          ...marketo
        }
      }
    }
    ... on Drupal_MenuLinkContentMainNavLinks {
      fieldKey
      fieldAnalyticsEventName
      fieldMarketo {
        entity {
          ...marketo
        }
      }
    }
  }

  fragment useMenuHook on Drupal_MenuLinkContent {
    entityUuid
    parent
    link {
      url {
        ...getLinkPath
      }
    }
    entityTranslations {
      entityLanguage {
        id
      }
      ... on Drupal_MenuLinkContent {
        title
      }
      ...useMenuDefaultFooter
      ...useMenuMainNavLinks
    }
  }
`;
